import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, map, catchError, throwError } from "rxjs";
import { ViewerConfigurationService } from "../../../viewer-components/services/viewer-configuration.service";
import { AgentKitService } from "../../../agent-ui";
import { PRINCIPAL_SERVICE } from "../../../viewer-components/services/principal.token";
import type { ThreadInfo, RawThread, RawThreadState } from "../model/thread.types";


/**
 * Direct HTTP access to the Agents API threads resource.
 * Bypasses the CopilotKit runtime — thread management is not proxied.
 *
 * Base URL: IViewerconfiguration.agentsApiUrl (trailing slash stripped automatically)
 * Auth: Bearer token read from AgentKitService.headers() — same source used by
 *       EndpointExecutionService for all agents-backend calls.
 */
@Injectable({ providedIn: "root" })
export class AgentsApiPrivateService {
  private readonly http = inject(HttpClient);
  private readonly viewerConfig = inject(ViewerConfigurationService);
  private readonly agentKit = inject(AgentKitService);
  private readonly principalService = inject(PRINCIPAL_SERVICE, { optional: true });

  private get baseUrl(): string {
    const url = this.viewerConfig.getViewerConfiguration()?.agentsApiUrl ?? "";
    return url.replace(/\/+$/, "");
  }

  private get headers(): HttpHeaders {
    const agentKitHeaders = this.agentKit.headers();
    return new HttpHeaders({
      ...agentKitHeaders,
      Accept: "application/json",
    });
  }

  /**
   * Searches threads via POST /threads/search filtered by agent and optionally by user.
   * Merges the caller-supplied metadata (e.g. { graph_id }) with the current user's
   * principal (metadata.owner) when CURRENT_USER_SERVICE is provided.
   */
  searchThreads(metadata: Record<string, string>): Observable<ThreadInfo[]> {
    const principal = this.principalService?.getPrincipal();
    const fullMetadata: Record<string, string> = principal
      ? { ...metadata, owner: principal }
      : metadata;
    return this.http
      .post<RawThread[]>(
        `${this.baseUrl}/threads/search`,
        { metadata: fullMetadata, limit: 100 },
        { headers: this.headers },
      )
      .pipe(
        map((res) => (Array.isArray(res) ? res : []).map((t) => this.mapThread(t))),
        catchError((err) => {
          console.error("AgentsApiPrivateService: failed to search threads", metadata, err);
          return throwError(() => err);
        }),
      );
  }

  /**
   * Fetches the full LangGraph state snapshot for a thread.
   * GET /threads/{thread_id}/state — includes the message history under `values.messages`.
   * Pass `checkpointNs` to read from a subgraph namespace (e.g. `"pipeline:<task_id>"`).
   */
  getThreadState(threadId: string, checkpointNs?: string): Observable<RawThreadState> {
    const params: Record<string, string> = checkpointNs ? { checkpoint_ns: checkpointNs } : {};
    return this.http
      .get<RawThreadState>(`${this.baseUrl}/threads/${encodeURIComponent(threadId)}/state`, {
        headers: this.headers,
        params,
      })
      .pipe(
        catchError((err) => {
          console.error("AgentsApiPrivateService: failed to load thread state", threadId, err);
          return throwError(() => err);
        }),
      );
  }

  /**
   * Fetches the current status of a subagent run.
   * GET /threads/{thread_id}/runs/{run_id}
   * Used by BackgroundJobPollingService to detect terminal states (success/error/timeout).
   */
  getRunStatus(threadId: string, runId: string): Observable<{ status: string; error_message: string | null }> {
    return this.http
      .get<{ status: string; error_message: string | null }>(
        `${this.baseUrl}/threads/${encodeURIComponent(threadId)}/runs/${encodeURIComponent(runId)}`,
        { headers: this.headers },
      )
      .pipe(
        catchError((err) => {
          console.error("AgentsApiPrivateService: failed to get run status", threadId, runId, err);
          return throwError(() => err);
        }),
      );
  }

  /**
   * Deletes a thread by id.
   * DELETE /threads/{thread_id} — cancels active runs before deletion.
   * Returns { "status": "deleted" } — mapped to void for callers.
   */
  deleteThread(threadId: string): Observable<void> {
    return this.http
      .delete<{ status: string }>(`${this.baseUrl}/threads/${encodeURIComponent(threadId)}`, { headers: this.headers })
      .pipe(
        map(() => undefined),
        catchError((err) => {
          console.error("AgentsApiPrivateService: failed to delete thread", threadId, err);
          return throwError(() => err);
        }),
      );
  }

  private mapThread(raw: RawThread): ThreadInfo {
    const parsed = new Date(raw.created_at);
    const createdAt = isNaN(parsed.getTime()) ? new Date() : parsed;
    const fallbackName = createdAt.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    const threadName = raw.name?.trim()
      || (typeof raw.metadata?.["thread_name"] === "string" ? raw.metadata["thread_name"].trim() : "")
      || fallbackName;
    return {
      threadId: raw.thread_id,
      name: threadName,
      createdAt,
    };
  }
}
